/**
 * Waktu per tahap satu giliran suara (#59 latensi): delegasi → token Claude
 * pertama → kalimat pertama terkirim → audio pertama, plus lama tool. Murni
 * angka di memori klien — tanpa teks, tanpa DB.
 */

import type { VoiceStageDurations as StageDurations } from "@/types/ai-assistant-voice";

export type { StageDurations };

export interface StageMarks {
	/** `session.delegation.created` diterima. */
	delegation: number;
	firstToken: number | null;
	firstSent: number | null;
	/** Audio GPT-Live pertama yang terdengar SETELAH kalimat pertama terkirim. */
	firstAudio: number | null;
	/** Total lama tool (dari `onStatus` sampai token berikutnya / akhir giliran). */
	toolMs: number;
}

const span = (from: number | null, to: number | null): number | null =>
	from === null || to === null ? null : Math.max(0, Math.round(to - from));

/** Durasi tiap tahap; null bila salah satu ujungnya tidak terjadi. */
export function stageDurations(m: StageMarks): StageDurations {
	return {
		delegationToTokenMs: span(m.delegation, m.firstToken),
		tokenToSentMs: span(m.firstToken, m.firstSent),
		sentToAudioMs: span(m.firstSent, m.firstAudio),
		totalMs: span(m.delegation, m.firstAudio),
		toolMs: m.toolMs > 0 ? Math.round(m.toolMs) : null,
	};
}

export interface StageTimer {
	token(): void;
	toolStarted(): void;
	sent(): void;
	/** true bila ini audio pertama yang dihitung (hanya setelah `sent`). */
	audio(at?: number): boolean;
	finish(): void;
	marks(): StageMarks;
}

export function createStageTimer(now: () => number): StageTimer {
	const m: StageMarks = {
		delegation: now(),
		firstToken: null,
		firstSent: null,
		firstAudio: null,
		toolMs: 0,
	};
	let toolSince: number | null = null;
	const closeTool = () => {
		if (toolSince === null) return;
		m.toolMs += now() - toolSince;
		toolSince = null;
	};
	return {
		token() {
			closeTool();
			m.firstToken ??= now();
		},
		toolStarted() {
			toolSince ??= now();
		},
		sent() {
			m.firstSent ??= now();
		},
		audio(at) {
			if (m.firstSent === null || m.firstAudio !== null) return false;
			m.firstAudio = at ?? now();
			return true;
		},
		finish: closeTool,
		marks: () => ({ ...m }),
	};
}

export interface TurnTiming {
	token(): void;
	toolStarted(): void;
	sent(): void;
	audio(at: number): void;
	/** Jawaban Claude tersimpan dengan id ini. */
	answered(messageId: string): void;
	/** Giliran selesai (hening); kirim apa adanya bila audio tak pernah terdengar. */
	settled(): void;
}

/** Timer satu giliran yang melapor sekali: saat id pesan & audio pertama sudah ada, atau saat selesai. */
export function createTurnTiming(
	now: () => number,
	emit: (messageId: string, durations: StageDurations) => void,
): TurnTiming {
	const timer = createStageTimer(now);
	let messageId: string | null = null;
	let heard = false;
	let emitted = false;
	const tryEmit = (force: boolean) => {
		if (emitted || messageId === null || (!force && !heard)) return;
		emitted = true;
		emit(messageId, stageDurations(timer.marks()));
	};
	return {
		token: timer.token,
		toolStarted: timer.toolStarted,
		sent: timer.sent,
		audio(at) {
			if (!timer.audio(at)) return;
			heard = true;
			tryEmit(false);
		},
		answered(id) {
			timer.finish();
			messageId = id;
			tryEmit(false);
		},
		settled: () => tryEmit(true),
	};
}
